/**
 * The world-date editor (1.Y deliverable 3, open point 2): a `world_date` field's date, formatted,
 * with "Set date" or "Edit date" (a When input taking points only) and "Clear", which asks first.
 * A save sends one `placements.create` with the field's id, or one `placements.update`.
 */
import type { FieldDefV1, PlacementV1 } from '@codex/contracts';
import { Button, Icon, Skeleton, useLayer } from '@codex/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useWhenContext } from '../when/calendar-context.js';
import { pointText } from '../when/pill-model.js';
import { WhenInput, type WhenInputState } from '../when/when-input.js';
import { refusalMessage } from '../when/when-model.js';
import { temporalKeys, useTemporalApi } from './api.js';
import { useTemporalEvents } from './events.js';
import {
  CANCEL,
  CLEAR,
  CLEAR_BODY,
  CLEAR_TITLE,
  DATE_LABEL,
  dateCleared,
  dateSaved,
  EDIT_DATE,
  NO_DATE,
  SAVE,
  SET_DATE,
  VALUES_FAILED,
} from './words.js';
import { createBody, fieldPlacements, updateBody, worldDateText } from './world-date-model.js';
import '../when/when.css';
import './temporal.css';

export interface WorldDateEditorProps {
  readonly docId: string;
  readonly field: FieldDefV1;
  /** A reference or trashed page: the date only, no control. */
  readonly readOnly?: boolean;
}

type Mode = 'view' | 'edit' | 'clear';

export function WorldDateEditor({ docId, field, readOnly = false }: WorldDateEditorProps) {
  const services = useTemporalApi();
  const client = useQueryClient();
  const ctx = useWhenContext();
  useTemporalEvents(docId);
  const [mode, setMode] = useState<Mode>('view');
  const [when, setWhen] = useState<WhenInputState | null>(null);
  const [busy, setBusy] = useState(false);
  /** A write is under way: Enter or a second click sends nothing more until it settles. */
  const inFlight = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  useLayer(mode !== 'view' && !busy, () => setMode('view'), 'temporal-form');
  const placementsQ = useQuery({
    queryKey: temporalKeys.placements(docId),
    queryFn: () => services.listPlacements(docId),
    retry: false,
  });

  if (placementsQ.isError) {
    return (
      <div className="cx-pills__field cx-temporal">
        <span className="cx-pills__label">{field.label}</span>
        <span className="cx-pills__hint" role="alert">
          <Icon name="warning" size={14} /> {VALUES_FAILED}
        </span>
      </div>
    );
  }
  if (!placementsQ.isSuccess || ctx.status === 'loading') {
    return (
      <div className="cx-pills__field cx-temporal">
        <span className="cx-pills__label">{field.label}</span>
        <Skeleton lines={1} label={`Loading ${field.label}`} />
      </div>
    );
  }
  const calendar = ctx.calendar;
  const mine = fieldPlacements(placementsQ.data, field.id);
  const placement: PlacementV1 | null = mine[0] ?? null;
  const editable = !readOnly && ctx.status === 'ready' && calendar !== null;
  const refresh = () => client.invalidateQueries({ queryKey: temporalKeys.placements(docId) });

  const run = async (write: () => Promise<unknown>, said: string) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      await write();
      setMode('view');
      setNotice(said);
      await refresh();
    } catch (failure) {
      setError(refusalMessage(failure).message);
      if (refusalMessage(failure).conflict) void refresh();
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const save = () => {
    const read = when?.read;
    if (!when?.ready || !read || calendar === null) return;
    if (placement === null) {
      const body = createBody(read.parsed, read.text, calendar, field.id);
      if (body !== null)
        void run(() => services.createPlacement(docId, body), dateSaved(field.label));
    } else {
      const body = updateBody(read.parsed, read.text, calendar, placement);
      if (body !== null) {
        void run(() => services.updatePlacement(docId, placement.id, body), dateSaved(field.label));
      }
    }
  };
  const clear = () => {
    if (placement === null) return;
    void run(
      () => services.removePlacement(docId, placement.id, placement.rv),
      dateCleared(field.label),
    );
  };

  return (
    <div
      className="cx-pills__field cx-temporal"
      data-testid="temporal.world-date"
      data-field-id={field.id}
    >
      <span className="cx-pills__label">{field.label}</span>
      <span className="cx-temporal__value" data-testid="temporal.world-date.text">
        {mine.length === 0 ? NO_DATE : mine.map((p) => worldDateText(calendar, p)).join('; ')}
      </span>
      {editable && mode === 'view' && (
        <div className="cx-temporal__actions">
          <Button
            size="sm"
            onClick={() => {
              setNotice('');
              setError(null);
              setMode('edit');
            }}
          >
            {placement === null ? SET_DATE : EDIT_DATE}
          </Button>
          {placement !== null && (
            <Button size="sm" onClick={() => setMode('clear')}>
              {CLEAR}
            </Button>
          )}
        </div>
      )}
      {editable && mode === 'edit' && (
        <fieldset className="cx-temporal__form" data-testid="temporal.form">
          <legend className="cx-temporal__legend">
            {placement === null ? SET_DATE : EDIT_DATE}
          </legend>
          <WhenInput
            docId={docId}
            label={DATE_LABEL}
            allow={['point', 'now']}
            {...(placement?.start ? { value: pointText(calendar, placement.start) } : {})}
            onRead={setWhen}
            onSubmit={save}
          />
          {error !== null && (
            <p className="cx-temporal__error" role="alert">
              <Icon name="warning" size={14} /> {error}
            </p>
          )}
          <div className="cx-temporal__actions">
            <Button
              variant="primary"
              size="sm"
              disabled={!when?.ready || busy}
              busy={busy}
              onClick={save}
            >
              {SAVE}
            </Button>
            <Button size="sm" disabled={busy} onClick={() => setMode('view')}>
              {CANCEL}
            </Button>
          </div>
        </fieldset>
      )}
      {editable && mode === 'clear' && (
        <fieldset className="cx-temporal__form">
          <legend className="cx-temporal__legend">{CLEAR_TITLE}</legend>
          <p className="cx-temporal__hint">{CLEAR_BODY}</p>
          {error !== null && (
            <p className="cx-temporal__error" role="alert">
              <Icon name="warning" size={14} /> {error}
            </p>
          )}
          <div className="cx-temporal__actions">
            <Button variant="danger" size="sm" busy={busy} onClick={clear}>
              {CLEAR}
            </Button>
            <Button size="sm" disabled={busy} onClick={() => setMode('view')}>
              {CANCEL}
            </Button>
          </div>
        </fieldset>
      )}
      <p className="cx-temporal__hint" role="status">
        {notice}
      </p>
    </div>
  );
}
